// src/components/commandSearch/CommandSearchProvider.jsx
//
// Command Search Provider
// =======================
//
// PURPOSE
// -------
// Owns global Command Search open/close state and keyboard shortcuts.
//
// WHY THIS EXISTS
// ---------------
// Command Search must not be owned by Navbar.
// Navbar is only one possible entry point.
//
// With a provider:
// - Navbar button can open Command Search,
// - Ctrl+K / Cmd+K can open it globally,
// - future buttons in other parts of the app can open it too,
// - the dialog is rendered once at application shell level.
//
// INTEGRATION
// -----------
// Wrap the authenticated application shell with this provider, for example:
//
//   <CommandSearchProvider user={authState.user}>
//     <Navbar />
//     <Sidebar />
//     <Outlet />
//   </CommandSearchProvider>
//
// If Navbar already lives inside a layout that has access to AuthContext, the
// provider can also read AuthContext directly. Passing `user` is simpler and
// keeps the provider reusable.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import CommandSearchDialog from "./CommandSearchDialog";
import AuthContext from "../../context/AuthContext";

const CommandSearchContext = createContext(null);

export function CommandSearchProvider({ children, user = null }) {
  const { authState } = useContext(AuthContext);

  /*
    Prefer explicitly provided user, but fall back to AuthContext.

    Why:
    - Navbar is no longer the owner of Command Search.
    - Command Search Provider lives at application shell level.
    - Static command role filtering needs access to the authenticated user role.
  */
  const effectiveUser = user || authState?.user || null;

  const [isOpen, setIsOpen] = useState(false);

  const openCommandSearch = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeCommandSearch = useCallback(() => {
    setIsOpen(false);
  }, []);

  /**
   * Global keyboard shortcut.
   *
   * Ctrl+K / Cmd+K opens Command Search.
   * "/" also opens it when the user is not typing in a form field.
   *
   * IMPORTANT:
   * Existing module-level "/" shortcuts may conflict. Because Command Search is
   * now intended to be global, this provider should be mounted only inside the
   * authenticated app shell and should be considered the owner of the global
   * shortcut. Module-local shortcuts can still exist, but then should use more
   * specific behavior or be removed later.
   */
  useEffect(() => {
    const onKeyDown = (event) => {
      const activeTag = document.activeElement?.tagName || "";
      const isTypingTarget = /^(INPUT|SELECT|TEXTAREA)$/.test(activeTag);

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        openCommandSearch();
        return;
      }

      if (
        event.key === "/" &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.metaKey &&
        !isTypingTarget
      ) {
        event.preventDefault();
        openCommandSearch();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => {
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [openCommandSearch]);

  const value = useMemo(
    () => ({
      isOpen,
      openCommandSearch,
      closeCommandSearch,
    }),
    [isOpen, openCommandSearch, closeCommandSearch]
  );

  return (
    <CommandSearchContext.Provider value={value}>
      {children}

      <CommandSearchDialog
        open={isOpen}
        onClose={closeCommandSearch}
        user={effectiveUser}
      />
    </CommandSearchContext.Provider>
  );
}

export function useCommandSearch() {
  const ctx = useContext(CommandSearchContext);

  if (!ctx) {
    throw new Error(
      "useCommandSearch must be used inside CommandSearchProvider."
    );
  }

  return ctx;
}
